"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Menu, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerHeader, DrawerTrigger, DrawerClose } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const filteredItems = menuItems.filter(item => item.label.toLowerCase() !== "our menu");
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-6">
        {filteredItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.label.toLowerCase() === "menu" ? "/Menu" : item.href || "#"} className="font-serif text-[14px] font-semibold uppercase tracking-widest text-foreground hover:text-primary transition-all duration-300 relative after:absolute after:bottom-[-4px] after:left-1/2 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full hover:after:left-0">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8 px-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const filteredItems = menuItems.filter(item => item.label.toLowerCase() !== "our menu");
  return <nav className="flex flex-col gap-6 mt-8 px-4">
      {filteredItems.map((item, i) => <div key={item.id} data-index={i}>
          <DrawerClose asChild>
            <Link to={item.label.toLowerCase() === "menu" ? "/Menu" : item.href || "#"} className="font-serif text-lg font-bold uppercase tracking-widest text-foreground hover:text-primary transition-colors block">
              {item.label}
            </Link>
          </DrawerClose>
        </div>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264094" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-secondary shadow-sm" : "bg-background")}>
      <div className="container mx-auto px-4 md:px-8 h-20">
        <div className="grid grid-cols-3 items-center h-full">
          
          <div className="justify-self-start flex items-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>

            <SafeHydrate><Drawer direction="left">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden -ml-2 text-foreground hover:bg-accent">
                  <Menu className="size-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-full w-[85vw] sm:w-[350px] border-r border-border bg-background">
                <DrawerHeader className="border-b border-border py-6 flex justify-start px-4">
                  <Link to="/HomePage">
                    <WvcLogo className="h-6 w-auto text-foreground font-serif" />
                  </Link>
                </DrawerHeader>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>

          <div className="justify-self-center">
            <Link to="/HomePage" className="block transition-opacity hover:opacity-80">
              <WvcLogo className="h-7 w-auto text-foreground font-sans" />
            </Link>
          </div>

          <div className="justify-self-end flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/50 rounded-lg">
              <Search className="size-5" />
              <span className="sr-only">Search</span>
            </Button>
          </div>

        </div>
      </div>
    </section>;
}